// Per-column statistics card. Adapted from database-creator-main@e533337
// frontend/src/wizard/StatsPanel.jsx (ColumnStatCard, Histogram, TopValues):
// one column instead of one table, imports from this app's Shared.jsx, and the
// histogram/top-value bars carry text alternatives.
import { G, G_BG, G_DARK } from "../../../components/Shared";

export function fmtNum(n) {
  if (n === undefined || n === null) return "–";
  return Number.isInteger(n) ? String(n) : Number(n).toFixed(2);
}

function Histogram({ buckets }) {
  if (!buckets || buckets.length === 0) {
    return <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No numeric values.</p>;
  }
  const max = Math.max(...buckets.map(b => b.count), 1);
  const summary = buckets.map(b => `${fmtNum(b.range_start)}–${fmtNum(b.range_end)}: ${b.count}`).join("; ");
  return (
    <div>
      <div role="img" aria-label={`Histogram. ${summary}`}
           style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
        {buckets.map((b, i) => (
          <div key={i} title={`${fmtNum(b.range_start)}–${fmtNum(b.range_end)}: ${b.count}`}
               style={{ flex: 1, height: `${Math.max((b.count / max) * 100, b.count > 0 ? 3 : 0)}%`,
                        background: G, borderRadius: "3px 3px 0 0" }} />
        ))}
      </div>
      <div aria-hidden="true" style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#6b7280", marginTop: 4 }}>
        <span>{fmtNum(buckets[0].range_start)}</span>
        <span>{fmtNum(buckets[buckets.length - 1].range_end)}</span>
      </div>
    </div>
  );
}

function TopValues({ items, distinctCount }) {
  if (!items || items.length === 0) {
    return <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No values.</p>;
  }
  const max = Math.max(...items.map(i => i.count), 1);
  const remaining = distinctCount - items.length;
  return (
    <ul style={{ display: "flex", flexDirection: "column", gap: 4, listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, alignItems: "center", fontSize: 12 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.value}>{it.value}</span>
          <div aria-hidden="true" style={{ background: G_BG, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${(it.count / max) * 100}%`, background: G, height: 10, borderRadius: 4 }} />
          </div>
          <span style={{ color: "#6b7280", fontFamily: "'JetBrains Mono', monospace" }}>{it.count}</span>
        </li>
      ))}
      {remaining > 0 && (
        <li style={{ fontSize: 11, color: "#6b7280" }}>+ {remaining} more distinct value{remaining > 1 ? "s" : ""}</li>
      )}
    </ul>
  );
}

export default function ColumnStats({ name, stat }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h4 style={{ fontSize: 13, margin: 0, color: G_DARK }}>{name}</h4>
        <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
          {stat.null_count} empty · {stat.distinct_count} distinct
        </span>
      </div>
      <div style={{ marginTop: 10 }}>
        {stat.kind === "numeric" && (
          <>
            <Histogram buckets={stat.histogram} />
            {stat.histogram && stat.histogram.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11, color: "#6b7280", marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                <span>min {fmtNum(stat.min)}</span>
                <span>max {fmtNum(stat.max)}</span>
                <span>mean {fmtNum(stat.mean)}</span>
                <span>median {fmtNum(stat.median)}</span>
              </div>
            )}
          </>
        )}
        {stat.kind === "categorical" && <TopValues items={stat.top_values} distinctCount={stat.distinct_count} />}
        {stat.kind === "date" && (
          <p style={{ fontSize: 12, color: "#374151", margin: 0 }}>
            {stat.min ? <>range: {stat.min} → {stat.max}</> : "No values."}
          </p>
        )}
        {stat.kind === "other" && <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No distribution shown for this type.</p>}
      </div>
    </div>
  );
}
